import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { UnsavedGuardBypassState } from '../../../../core-components/theme/components/Screen';
import { useMinuteTick } from '../../../../utilities/customHooks/useMinuteTick';
import { CTL_ROUTES } from '../../../../navigation/paths';
import { activeAlert, alertKey, canStartFrom, IN_APP_FRESH_MS, isStillOpen } from '../../domain/inAppAlerts';
import type { ActiveAlert } from '../../domain/inAppAlerts';
import { recordLinkOf, resolveLink } from '../../domain/links';
import type { ResolvedLink } from '../../domain/links';
import {
  useDismissedAlerts,
  useInAppOpenedAt,
  useInAppSessionActions,
  useInAppSessionStore,
  useSeenLiveAlerts,
} from '../../store/inAppSessionStore';
import type { FeedItem, IssueStartResult, StartedBy } from '../../types/domain.types';
import { myFeedQueryOptions } from '../../utils/queryOptions';
import { warnHooterNotSilenced } from '../start/hooterWarning';
import { useStartIssue } from '../start/useStartIssue';

import { useIsOnline } from './useIsOnline';
import { useLatest } from './useLatest';
import { usePlantLabel } from './usePlantLabel';

export type OpenTarget = ResolvedLink['kind'] | null;

export type AlertPhase =
  | { readonly kind: 'live' }
  | { readonly kind: 'starting' }
  | { readonly kind: 'taken'; readonly by: StartedBy | null }
  | { readonly kind: 'closing' };

export interface ShownAlert extends ActiveAlert {
  readonly phase: AlertPhase;
}

export interface InAppAlertsLogic {
  readonly shown: ShownAlert | null;
  readonly announced: FeedItem | null;
  readonly now: number;
  readonly openTarget: OpenTarget;
  readonly plantName: string | null;
  readonly canStart: boolean;
  readonly startFailed: boolean;
  readonly offline: boolean;
  readonly start: () => void;
  readonly dismiss: () => void;
  readonly expire: () => void;
  readonly open: () => void;
  readonly seeAll: () => void;
  readonly closed: () => void;
  readonly shownLive: () => void;
}

export const TAKEN_VISIBLE_MS = 4_000;

const PAST_THE_GUARD: UnsavedGuardBypassState = { bypassUnsavedGuard: true };

type Held =
  | { readonly reason: 'starting'; readonly alert: ActiveAlert }
  | { readonly reason: 'closing'; readonly alert: ActiveAlert }
  | { readonly reason: 'taken'; readonly alert: ActiveAlert; readonly by: StartedBy | null };

const LIVE: AlertPhase = { kind: 'live' };
const STARTING: AlertPhase = { kind: 'starting' };
const CLOSING: AlertPhase = { kind: 'closing' };
const NOTHING_HIDDEN: ReadonlySet<string> = new Set();

const phaseOf = (held: Held): AlertPhase => {
  switch (held.reason) {
    case 'starting':
      return STARTING;
    case 'closing':
      return CLOSING;
    case 'taken':
      return { kind: 'taken', by: held.by };
  }
};

const isFresh = (item: FeedItem, openedAt: number): boolean => {
  const at = Date.parse(item.at);
  return !Number.isNaN(at) && at >= openedAt - IN_APP_FRESH_MS;
};

export interface SessionMarks {
  readonly dismissed: ReadonlySet<string>;
  readonly seenLive: ReadonlySet<string>;
  readonly openedAt: number;
}

export const startedByTeammate = (items: readonly FeedItem[], { dismissed, seenLive, openedAt }: SessionMarks): ShownAlert | null => {
  const item = items.find(
    (candidate) =>
      candidate.fullScreenAlarm &&
      candidate.state === 'ALERTED' &&
      candidate.startedBy !== null &&
      isStillOpen(candidate) &&
      isFresh(candidate, openedAt) &&
      seenLive.has(alertKey(candidate)) &&
      !dismissed.has(alertKey(candidate)),
  );
  if (item === undefined) return null;
  return { kind: 'emergency', item, pendingCount: 0, emergencyCount: 0, phase: { kind: 'taken', by: item.startedBy } };
};

export const shownAlertOf = (items: readonly FeedItem[], marks: SessionMarks, held: Held | null): ShownAlert | null => {
  if (held !== null) return { ...held.alert, phase: phaseOf(held) };
  const active = activeAlert(items, marks.dismissed, marks.openedAt);
  if (active?.kind === 'emergency') return { ...active, phase: LIVE };
  const teammate = startedByTeammate(items, marks);
  if (teammate !== null) return teammate;
  return active === null ? null : { ...active, phase: LIVE };
};

const hiddenOf = (dismissed: ReadonlySet<string>, timedOut: ReadonlySet<string>): ReadonlySet<string> =>
  timedOut.size === 0 ? dismissed : new Set([...dismissed, ...timedOut]);

const targetOf = (item: FeedItem): ResolvedLink | null => {
  const link = recordLinkOf(item);
  return link === null ? null : resolveLink(link, window.location.origin);
};

const heldAlertOf = (shown: ShownAlert): ActiveAlert => ({
  kind: shown.kind,
  item: shown.item,
  pendingCount: shown.pendingCount,
  emergencyCount: shown.emergencyCount,
});

const answeredElsewhere = (alert: ActiveAlert): boolean => useInAppSessionStore.getState().dismissed.has(alertKey(alert.item));

const isAnswerable = (shown: ShownAlert | null): shown is ShownAlert => shown !== null && shown.phase.kind !== 'closing';

export const useInAppAlerts = (): InAppAlertsLogic => {
  const { t } = useTranslation();
  const now = useMinuteTick();
  const online = useIsOnline();
  const feed = useQuery(myFeedQueryOptions());
  const dismissed = useDismissedAlerts();
  const seenLive = useSeenLiveAlerts();
  const openedAt = useInAppOpenedAt();
  const { dismiss: dismissKey, sawLive } = useInAppSessionActions();
  const navigate = useNavigate();
  const [held, setHeld] = useState<Held | null>(null);
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState<ReadonlySet<string>>(NOTHING_HIDDEN);
  const [startingIssueId, setStartingIssueId] = useState<string | null>(null);

  const hidden = hiddenOf(dismissed, timedOut);
  const shown = shownAlertOf(feed.data?.items ?? [], { dismissed: hidden, seenLive, openedAt }, held);
  const shownKey = shown === null ? null : alertKey(shown.item);
  const plantName = usePlantLabel(shown?.kind === 'emergency' && shown.item.plantName === null ? shown.item.plantId : null);
  const startIssue = useStartIssue(startingIssueId ?? shown?.item.issueId ?? '');

  const close = (alert: ActiveAlert): void => {
    dismissKey(alertKey(alert.item));
    setHeld({ reason: 'closing', alert });
  };

  const answer = (): ActiveAlert | null => {
    if (!isAnswerable(shown)) return null;
    const alert = heldAlertOf(shown);
    close(alert);
    return alert;
  };

  const openRecord = (alert: ActiveAlert): void => {
    const target = targetOf(alert.item);
    if (target === null) return;
    if (target.kind === 'external') {
      window.open(target.href, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(target.path, alert.kind === 'emergency' ? { state: PAST_THE_GUARD } : undefined);
  };

  const warnIfStillSounding = (alert: ActiveAlert, result: IssueStartResult): void => {
    if (alert.kind === 'emergency' && result.outcome === 'STARTED' && !result.hooterSilenced) warnHooterNotSilenced(t);
  };

  const settleStart = (alert: ActiveAlert, result: IssueStartResult): void => {
    warnIfStillSounding(alert, result);
    if (answeredElsewhere(alert)) return;
    switch (result.outcome) {
      case 'STARTED':
        close(alert);
        openRecord(alert);
        return;
      case 'ALREADY_STARTED':
        setHeld({ reason: 'taken', alert, by: result.startedBy });
        return;
      case 'NOT_FOUND':
        close(alert);
    }
  };

  const takenKey = shown?.phase.kind === 'taken' ? shownKey : null;
  const closeShown = useLatest((): void => {
    if (shown !== null) close(heldAlertOf(shown));
  });
  useEffect(() => {
    if (takenKey === null) return undefined;
    const timer = window.setTimeout(() => closeShown.current(), TAKEN_VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [takenKey, closeShown]);

  return {
    shown,
    announced: shown?.kind === 'banner' && shown.phase.kind === 'live' ? shown.item : null,
    now,
    openTarget: shown === null ? null : targetOf(shown.item)?.kind ?? null,
    plantName: shown?.item.plantName ?? plantName,
    canStart: shown !== null && canStartFrom(shown.item) && (shown.phase.kind === 'live' || shown.phase.kind === 'starting'),
    startFailed: failedKey !== null && failedKey === shownKey && shown?.phase.kind === 'live',
    offline: !online,
    start: () => {
      if (shown === null || shown.phase.kind !== 'live' || !canStartFrom(shown.item) || startIssue.isPending) return;
      const alert = heldAlertOf(shown);
      setFailedKey(null);
      setHeld({ reason: 'starting', alert });
      setStartingIssueId(alert.item.issueId);
      startIssue.mutate('APP', {
        onSuccess: (result) => settleStart(alert, result),
        onError: () => {
          if (answeredElsewhere(alert)) return;
          setHeld(null);
          setFailedKey(alertKey(alert.item));
        },
        onSettled: () => setStartingIssueId(null),
      });
    },
    dismiss: () => {
      answer();
    },
    expire: () => {
      if (shown === null || shown.kind !== 'banner' || (shown.phase.kind !== 'live' && shown.phase.kind !== 'taken')) return;
      const key = alertKey(shown.item);
      setTimedOut((current) => new Set(current).add(key));
      setHeld({ reason: 'closing', alert: heldAlertOf(shown) });
    },
    open: () => {
      const answered = answer();
      if (answered !== null) openRecord(answered);
    },
    seeAll: () => {
      answer();
      navigate(CTL_ROUTES.recent);
    },
    closed: () => setHeld((current) => (current?.reason === 'closing' ? null : current)),
    shownLive: () => {
      if (shown?.kind === 'emergency' && shown.phase.kind === 'live') sawLive(alertKey(shown.item));
    },
  };
};
